/**
 * PLAYER INSIGHTS MODAL
 * 
 * AI-powered player research with tabs:
 * - Intel: Cached insights, news, injury status
 * - Ask AI: Chat with AI about this player  
 * - Community: Talk smack, share tips
 */

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, Brain, MessageCircle, Users, Search, Send, Loader,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Zap, Star, ThumbsUp, Info
} from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { API_BASE_URL } from '../constants'

interface Player {
  id: string
  name: string
  team: string
  position: string
  salary?: number
  injury?: string
}

interface PlayerInsightsModalProps {
  isOpen: boolean
  onClose: () => void
  player: Player | null
  contestId: string | number
  userId?: number
  username?: string
  scoringRules?: any[]
}

interface Insights {
  summary?: string
  strengths?: string[]
  weaknesses?: string[]
  recentNews?: Array<{ headline: string; sentiment: string }>
  injuryStatus?: string
  matchupAnalysis?: string
  projectedPoints?: number
  projectionReasoning?: string
  riskLevel?: string
  startSitRecommendation?: string
}

interface ChatMessage {
  id: number
  user_id: number
  username: string
  message: string
  message_type: string
  created_at: string
}

export default function PlayerInsightsModal({
  isOpen,
  onClose,
  player,
  contestId,
  userId,
  username,
  scoringRules
}: PlayerInsightsModalProps) {
  const [activeTab, setActiveTab] = useState<'stats' | 'intel' | 'chat'>('stats')
  
  // Stats tab state (from API-Sports)
  const [playerStats, setPlayerStats] = useState<any>(null)
  const [loadingStats, setLoadingStats] = useState(false)
  
  // Intel tab state
  const [insights, setInsights] = useState<Insights | null>(null)
  const [hasInsights, setHasInsights] = useState(false)
  const [loadingInsights, setLoadingInsights] = useState(false)
  const [isResearching, setIsResearching] = useState(false)
  const [researchedBy, setResearchedBy] = useState<string | null>(null)
  
  // AI chat tab state
  const [aiQuestion, setAiQuestion] = useState('')
  const [aiConversation, setAiConversation] = useState<Array<{ role: 'user' | 'ai'; content: string }>>([])
  const [isAskingAI, setIsAskingAI] = useState(false)
  
  // Community chat tab state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loadingChat, setLoadingChat] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  
  const chatEndRef = useRef<HTMLDivElement>(null)
  
  // Load data when modal opens
  useEffect(() => {
    if (isOpen && player) {
      loadStats()
      loadInsights()
      loadChat()
    }
  }, [isOpen, player, contestId])
  
  // Load player stats from API-Sports
  const loadStats = async () => {
    if (!player) return
    
    try {
      setLoadingStats(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/player/${player.id}/stats`
      )
      
      if (response.data.success) {
        setPlayerStats(response.data.player)
      }
    } catch (error) {
      console.error('Failed to load player stats:', error)
      // Still show basic info even if API fails
      setPlayerStats({
        name: player.name,
        position: player.position,
        team: player.team
      })
    } finally {
      setLoadingStats(false)
    }
  }
  
  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, aiConversation])
  
  const loadInsights = async () => {
    if (!player) return
    
    try {
      setLoadingInsights(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/player/${player.id}/insights`
      )
      
      if (response.data.hasInsights) {
        setInsights(response.data.insights.insights_json)
        setHasInsights(true)
        setResearchedBy(response.data.insights.researched_by_username)
      } else {
        setInsights(null)
        setHasInsights(false)
      }
    } catch (error) {
      console.error('Failed to load insights:', error)
    } finally {
      setLoadingInsights(false)
    }
  }
  
  const doResearch = async () => {
    if (!player || !userId) {
      toast.error('Please log in to research players')
      return
    }
    
    try {
      setIsResearching(true)
      toast.loading('🔬 Researching player...', { id: 'research' })
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/player/${player.id}/research`,
        {
          userId,
          username,
          playerName: player.name,
          team: player.team,
          position: player.position
        }
      )
      
      if (response.data.success) {
        setInsights(response.data.insights.insights_json)
        setHasInsights(true)
        setResearchedBy(username || 'You')
        toast.success('Research complete!', { id: 'research' })
      }
    } catch (error: any) {
      console.error('Research failed:', error)
      toast.error('Research failed: ' + (error.response?.data?.error || error.message), { id: 'research' })
    } finally {
      setIsResearching(false)
    }
  }
  
  const askAI = async () => {
    if (!aiQuestion.trim() || !player) return
    
    const question = aiQuestion
    setAiQuestion('')
    setAiConversation(prev => [...prev, { role: 'user', content: question }])
    
    try {
      setIsAskingAI(true)
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/player/${player.id}/ask-ai`,
        {
          question,
          playerName: player.name,
          team: player.team,
          position: player.position
        }
      )
      
      if (response.data.success) {
        setAiConversation(prev => [...prev, { role: 'ai', content: response.data.answer }])
      }
    } catch (error: any) {
      console.error('AI question failed:', error)
      setAiConversation(prev => [...prev, { role: 'ai', content: 'Sorry, I couldn\'t process that question. Please try again.' }])
    } finally {
      setIsAskingAI(false)
    }
  }
  
  const loadChat = async () => {
    if (!player) return
    
    try {
      setLoadingChat(true)
      const response = await axios.get(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/player/${player.id}/chat`
      )
      setChatMessages(response.data.messages || [])
    } catch (error) {
      console.error('Failed to load chat:', error)
    } finally {
      setLoadingChat(false)
    }
  }
  
  const sendChatMessage = async () => {
    if (!newMessage.trim() || !player || !userId) return
    
    const message = newMessage
    setNewMessage('')
    
    try {
      setSendingMessage(true)
      
      const response = await axios.post(
        `${API_BASE_URL}/api/fantasy/contests/${contestId}/player/${player.id}/chat`,
        {
          userId,
          username,
          message,
          messageType: 'chat'
        }
      )
      
      if (response.data.success) {
        setChatMessages(prev => [...prev, response.data.message])
      }
    } catch (error: any) {
      console.error('Failed to send message:', error)
      toast.error('Failed to send message')
    } finally {
      setSendingMessage(false)
    }
  }
  
  if (!isOpen || !player) return null
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-2xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <span className="text-lg font-bold text-emerald-400">{player.position}</span>
                </div>
                <div>
                  <h2 className="font-bold text-white text-lg">{player.name}</h2>
                  <p className="text-sm text-gray-400">
                    {player.team} • ${player.salary?.toLocaleString()}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setActiveTab('stats')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'stats'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <Info className="w-4 h-4" />
                Stats
              </button>
              <button
                onClick={() => setActiveTab('intel')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'intel'
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <Brain className="w-4 h-4" />
                Intel & AI
                {hasInsights && <CheckCircle className="w-3 h-3 text-green-400" />}
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'chat'
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-800 text-gray-400 hover:text-white'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Community
                {chatMessages.length > 0 && (
                  <span className="text-xs bg-slate-700 px-1.5 rounded">{chatMessages.length}</span>
                )}
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            {/* STATS TAB - Real data from API-Sports */}
            {activeTab === 'stats' && (
              <div>
                {loadingStats ? (
                  <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
                    <p className="text-gray-400 mt-2">Loading player data...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Player Header with Photo */}
                    <div className="flex items-center gap-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-4">
                      {playerStats?.image ? (
                        <img 
                          src={playerStats.image} 
                          alt={player.name}
                          className="w-20 h-20 rounded-xl object-cover bg-slate-700"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-20 h-20 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                          <span className="text-2xl font-bold text-emerald-400">{player.position}</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-white">{player.name}</h2>
                        <p className="text-gray-400">{player.team} • {player.position}</p>
                        {playerStats?.number && (
                          <p className="text-emerald-400 font-semibold">#{playerStats.number}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-emerald-400">
                          ${player.salary?.toLocaleString()}
                        </div>
                        <p className="text-xs text-gray-500">Salary</p>
                      </div>
                    </div>
                    
                    {/* Player Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {playerStats?.age && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Age</p>
                          <p className="text-lg font-bold text-white">{playerStats.age}</p>
                        </div>
                      )}
                      {playerStats?.height && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Height</p>
                          <p className="text-lg font-bold text-white">{playerStats.height}</p>
                        </div>
                      )}
                      {playerStats?.weight && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Weight</p>
                          <p className="text-lg font-bold text-white">{playerStats.weight}</p>
                        </div>
                      )}
                      {playerStats?.experience && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Experience</p>
                          <p className="text-lg font-bold text-white">{playerStats.experience} yrs</p>
                        </div>
                      )}
                      {playerStats?.college && (
                        <div className="bg-slate-800/50 rounded-lg p-3 text-center col-span-2">
                          <p className="text-xs text-gray-500">College</p>
                          <p className="text-lg font-bold text-white">{playerStats.college}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Injury Status */}
                    {(playerStats?.injury || player.injury) && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <div>
                          <span className="font-semibold text-red-400">INJURY</span>
                          <p className="text-sm text-gray-300">{playerStats?.injury || player.injury}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Season Stats - Position-aware display */}
                    {playerStats?.stats && Object.keys(playerStats.stats).length > 0 && (
                      <div className="space-y-4">
                        {/* Games Played */}
                        {playerStats.games_played && (
                          <div className="flex items-center gap-2 text-sm text-gray-400">
                            <span className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                              {playerStats.games_played} Games Played
                            </span>
                          </div>
                        )}
                        
                        {/* Passing Stats (QBs) */}
                        {playerStats.stats.passing_yards !== undefined && (
                          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                            <h3 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
                              <Zap className="w-4 h-4" />
                              Passing
                            </h3>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                              <StatBox label="Yards" value={playerStats.stats.passing_yards?.toLocaleString()} color="blue" />
                              <StatBox label="TDs" value={playerStats.stats.passing_tds} color="emerald" />
                              <StatBox label="INTs" value={playerStats.stats.interceptions} color="red" />
                              <StatBox label="Comp %" value={playerStats.stats.completion_pct} color="blue" />
                              {playerStats.stats.passer_rating && (
                                <StatBox label="Rating" value={playerStats.stats.passer_rating} color="amber" />
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Rushing Stats */}
                        {playerStats.stats.rushing_yards !== undefined && (
                          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                            <h3 className="font-semibold text-orange-400 mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4" />
                              Rushing
                            </h3>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                              <StatBox label="Yards" value={playerStats.stats.rushing_yards?.toLocaleString()} color="orange" />
                              <StatBox label="TDs" value={playerStats.stats.rushing_tds} color="emerald" />
                              <StatBox label="Carries" value={playerStats.stats.rushing_attempts} color="gray" />
                              <StatBox label="YPC" value={playerStats.stats.yards_per_carry} color="orange" />
                            </div>
                          </div>
                        )}
                        
                        {/* Receiving Stats */}
                        {playerStats.stats.receptions !== undefined && (
                          <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                            <h3 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                              <Star className="w-4 h-4" />
                              Receiving
                            </h3>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                              <StatBox label="Rec" value={playerStats.stats.receptions} color="purple" />
                              <StatBox label="Yards" value={playerStats.stats.receiving_yards?.toLocaleString()} color="purple" />
                              <StatBox label="TDs" value={playerStats.stats.receiving_tds} color="emerald" />
                              <StatBox label="Targets" value={playerStats.stats.targets} color="gray" />
                              <StatBox label="YPC" value={playerStats.stats.yards_per_catch} color="purple" />
                            </div>
                          </div>
                        )}
                        
                        {/* Kicking Stats */}
                        {playerStats.stats.field_goals_made !== undefined && (
                          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                            <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Kicking
                            </h3>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                              <StatBox label="FG Made" value={playerStats.stats.field_goals_made} color="amber" />
                              <StatBox label="FG Att" value={playerStats.stats.field_goals_att} color="gray" />
                              <StatBox label="FG %" value={playerStats.stats.fg_pct} color="amber" />
                              <StatBox label="XP Made" value={playerStats.stats.extra_points_made} color="emerald" />
                              {playerStats.stats.longest_fg && (
                                <StatBox label="Long" value={`${playerStats.stats.longest_fg} yds`} color="amber" />
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Scoring Totals */}
                        {playerStats.stats.total_tds && playerStats.stats.total_tds > 0 && (
                          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                            <h3 className="font-semibold text-emerald-400 mb-3">Season Scoring</h3>
                            <div className="grid grid-cols-2 gap-3">
                              <StatBox label="Total TDs" value={playerStats.stats.total_tds} color="emerald" />
                              {playerStats.stats.two_pt_conversions && (
                                <StatBox label="2PT Conv" value={playerStats.stats.two_pt_conversions} color="emerald" />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* No detailed stats message */}
                    {(!playerStats?.stats || Object.keys(playerStats.stats).length === 0) && (
                      <div className="bg-slate-800/30 rounded-lg p-4 text-center">
                        <p className="text-gray-400 text-sm">
                          Season stats not yet available for this player.
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          Stats update daily during the NFL season.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* INTEL TAB */}
            {activeTab === 'intel' && (
              <div>
                {loadingInsights ? (
                  <div className="text-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                    <p className="text-gray-400 mt-2">Loading insights...</p>
                  </div>
                ) : hasInsights && insights ? (
                  <div className="space-y-4">
                    {/* Researched by */}
                    {researchedBy && (
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <Star className="w-3 h-3 text-amber-500" />
                        Researched by {researchedBy}
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h3 className="font-semibold text-white mb-2">Summary</h3>
                      <p className="text-gray-300 text-sm">{insights.summary}</p>
                    </div>
                    
                    {/* Projection */}
                    {insights.projectedPoints && (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-emerald-400">Projected Points</h3>
                          <span className="text-2xl font-bold text-emerald-400">
                            {insights.projectedPoints.toFixed(1)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400">{insights.projectionReasoning}</p>
                      </div>
                    )}
                    
                    {/* Strengths & Weaknesses */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {insights.strengths && insights.strengths.length > 0 && (
                        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                          <h3 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Strengths
                          </h3>
                          <ul className="space-y-1">
                            {insights.strengths.map((s, i) => (
                              <li key={i} className="text-sm text-gray-300">• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {insights.weaknesses && insights.weaknesses.length > 0 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                          <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                            <TrendingDown className="w-4 h-4" />
                            Weaknesses
                          </h3>
                          <ul className="space-y-1">
                            {insights.weaknesses.map((w, i) => (
                              <li key={i} className="text-sm text-gray-300">• {w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Injury Status */}
                    {insights.injuryStatus && insights.injuryStatus !== 'healthy' && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                        <div>
                          <span className="font-semibold text-yellow-400">
                            {insights.injuryStatus.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Start/Sit */}
                    {insights.startSitRecommendation && (
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        <h3 className="font-semibold text-white mb-2">Recommendation</h3>
                        <p className="text-gray-300 text-sm">{insights.startSitRecommendation}</p>
                      </div>
                    )}
                    
                    {/* Refresh button */}
                    <button
                      onClick={doResearch}
                      disabled={isResearching}
                      className="w-full py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-all"
                    >
                      {isResearching ? 'Updating...' : '🔄 Refresh Research'}
                    </button>
                    
                    {/* AI Chat Section - Integrated into Intel tab */}
                    <div className="mt-6 pt-4 border-t border-slate-700">
                      <h3 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Chat with AI about {player.name}
                      </h3>
                      
                      {/* Conversation */}
                      <div className="max-h-[200px] overflow-y-auto space-y-2 mb-3">
                        {aiConversation.length === 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {[
                              'Should I start or sit?',
                              'What\'s the matchup like?',
                              'Floor vs ceiling?'
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => setAiQuestion(suggestion)}
                                className="px-3 py-1.5 bg-slate-800 text-gray-400 rounded-lg text-xs hover:bg-slate-700 hover:text-white transition-all"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        ) : (
                          aiConversation.map((msg, i) => (
                            <div
                              key={i}
                              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                  msg.role === 'user'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-slate-800 text-gray-200'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          ))
                        )}
                        {isAskingAI && (
                          <div className="flex justify-start">
                            <div className="bg-slate-800 rounded-lg px-3 py-2">
                              <Loader className="w-4 h-4 animate-spin text-purple-400" />
                            </div>
                          </div>
                        )}
                        <div ref={chatEndRef} />
                      </div>
                      
                      {/* Input */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={aiQuestion}
                          onChange={e => setAiQuestion(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && askAI()}
                          placeholder={`Ask anything about ${player.name}...`}
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500"
                        />
                        <button
                          onClick={askAI}
                          disabled={!aiQuestion.trim() || isAskingAI}
                          className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-400 disabled:opacity-50 transition-all"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Brain className="w-16 h-16 text-blue-500/30 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Be the first to research!</h3>
                    <p className="text-gray-400 mb-6">
                      Get AI-powered insights about {player.name}'s fantasy value
                    </p>
                    <button
                      onClick={doResearch}
                      disabled={isResearching || !userId}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-500 hover:to-purple-500 transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                    >
                      {isResearching ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Researching...
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5" />
                          Research {player.name}
                        </>
                      )}
                    </button>
                    {!userId && (
                      <p className="text-sm text-gray-500 mt-2">Connect wallet to research</p>
                    )}
                    
                    {/* AI Chat - Available even without research */}
                    <div className="mt-8 pt-4 border-t border-slate-700">
                      <h3 className="font-semibold text-purple-400 mb-3 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Or just ask AI about {player.name}
                      </h3>
                      
                      {/* Conversation */}
                      <div className="max-h-[150px] overflow-y-auto space-y-2 mb-3">
                        {aiConversation.length === 0 ? (
                          <div className="flex flex-wrap gap-2 justify-center">
                            {[
                              'Tell me about this player',
                              'Fantasy value?',
                              'Recent performance?'
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => setAiQuestion(suggestion)}
                                className="px-3 py-1.5 bg-slate-800 text-gray-400 rounded-lg text-xs hover:bg-slate-700 hover:text-white transition-all"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        ) : (
                          aiConversation.map((msg, i) => (
                            <div
                              key={i}
                              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[85%] rounded-lg px-3 py-2 ${
                                  msg.role === 'user'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-slate-800 text-gray-200'
                                }`}
                              >
                                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              </div>
                            </div>
                          ))
                        )}
                        {isAskingAI && (
                          <div className="flex justify-start">
                            <div className="bg-slate-800 rounded-lg px-3 py-2">
                              <Loader className="w-4 h-4 animate-spin text-purple-400" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Input */}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={aiQuestion}
                          onChange={e => setAiQuestion(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && askAI()}
                          placeholder={`Ask about ${player.name}...`}
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-gray-500 focus:border-purple-500"
                        />
                        <button
                          onClick={askAI}
                          disabled={!aiQuestion.trim() || isAskingAI}
                          className="px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-400 disabled:opacity-50 transition-all"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* COMMUNITY CHAT TAB */}
            {activeTab === 'chat' && (
              <div className="flex flex-col h-[50vh]">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-3 mb-4">
                  {loadingChat ? (
                    <div className="text-center py-8">
                      <Loader className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-amber-500/30 mx-auto mb-3" />
                      <p className="text-gray-400">Be the first to talk about {player.name}!</p>
                      <p className="text-sm text-gray-500 mt-1">Share tips, predictions, or talk smack</p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.user_id === userId ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-4 py-2 ${
                            msg.user_id === userId
                              ? 'bg-amber-500 text-white'
                              : 'bg-slate-800 text-gray-200'
                          }`}
                        >
                          {msg.user_id !== userId && (
                            <p className="text-xs text-amber-400 font-semibold mb-1">{msg.username}</p>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <p className="text-[10px] opacity-60 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>
                
                {/* Input */}
                {userId ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && sendChatMessage()}
                      placeholder={`Talk about ${player.name}...`}
                      className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500"
                    />
                    <button
                      onClick={sendChatMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-all"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <p className="text-center text-sm text-gray-500">Connect wallet to chat</p>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

// Helper component for stat display boxes
function StatBox({ label, value, color = 'emerald' }: { label: string; value: any; color?: string }) {
  const colorClasses: Record<string, string> = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    orange: 'text-orange-400',
    purple: 'text-purple-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    gray: 'text-gray-400'
  }
  
  return (
    <div className="text-center bg-slate-900/50 rounded-lg p-2">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className={`text-lg font-bold ${colorClasses[color] || colorClasses.emerald}`}>
        {value ?? '-'}
      </p>
    </div>
  )
}

